import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Heading, Stack, Text } from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { MemberActions } from './MemberActions';
import MemberOrganisationTag from './MemberOrganisationTag';
import ShowMoreOrganisations from './ShowMoreOrganisations';
import { Member } from '../../apollo/queries';
import {
  ACTIONS,
  E_MAIL,
  FIRST_NAME,
  LAST_NAME,
  MAX_ORG_CODES,
  ORGANISATION,
  STATUS,
} from '../../utils/constants';
import { Status } from '../Organisations';

const StyledTagList = styled(Stack)`
  > div:first-child {
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
  }
`;

export interface MembersColumnsOptions {
  refetch: () => void;
  isViewOnlyRole: boolean;
}

function getOrganisationsToShow(memberships: Member['membershipsV2']) {
  return memberships.slice(0, MAX_ORG_CODES);
}

function getRemainingOrganisations(memberships: Member['membershipsV2']) {
  return memberships.slice(MAX_ORG_CODES);
}

export const getMembersColumns = ({
  refetch,
  isViewOnlyRole,
}: MembersColumnsOptions): ColumnDef<Member>[] => {
  const baseColumns: ColumnDef<Member>[] = [
    {
      header: FIRST_NAME,
      accessorKey: 'firstName',
      size: 120,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: LAST_NAME,
      accessorKey: 'lastName',
      size: 120,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: E_MAIL,
      accessorKey: 'email',
      size: 200,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: ORGANISATION,
      id: 'organisation',
      accessorFn: (row) => row.membershipsV2,
      size: 200,
      cell: (info) => {
        const membershipsV2 = info.row.original.membershipsV2;
        const organisationsToShow = getOrganisationsToShow(membershipsV2);
        const remaining = getRemainingOrganisations(membershipsV2);
        const userId = info.row.original.id;

        return (
          <StyledTagList
            flexDirection="column"
            justifyContent="center"
            className="organisation"
          >
            {organisationsToShow.map((membership) => (
              <MemberOrganisationTag
                orgCode={membership.organisation}
                key={`organisation-tag-${membership.organisation}-${userId}`}
                userId={userId}
              />
            ))}
            {remaining.length > 0 && (
              <ShowMoreOrganisations memberships={remaining} />
            )}
          </StyledTagList>
        );
      },
    },
    {
      header: STATUS,
      accessorKey: 'enabled',
      size: 80,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Status isActive={info.getValue() as boolean} />
        </Stack>
      ),
    },
  ];

  if (!isViewOnlyRole) {
    baseColumns.push({
      header: ACTIONS,
      id: 'actions',
      size: 66,
      cell: ({ row }) => (
        <MemberActions member={row.original} refetch={refetch} />
      ),
    });
  }

  return baseColumns;
};
